import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  GearSix, Package, MicrophoneStage, Users, Crown, Plus, Trash, UploadSimple, Star, Stack,
  CheckCircle, XCircle,
} from "@phosphor-icons/react";

const TYPES = [
  { value: "icon_pack", label: "Icon Pack" },
  { value: "widget", label: "Widget" },
  { value: "wallpaper", label: "Wallpaper" },
];

const emptyItem = { type: "icon_pack", title: "", description: "", category: "", preview_image: "", featured: false, price_label: "Premium" };
const emptyBit = { title: "", content: "", author: "D&J", category: "Bit" };
const emptyBundle = { title: "", description: "", cover_image: "", item_ids: [], featured: false };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full bg-[#05050A] border border-white/10 rounded-sm px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#00E5FF] transition-colors";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("items");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [bits, setBits] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [subs, setSubs] = useState({ items: [], bundles: [] });
  const [itemForm, setItemForm] = useState(emptyItem);
  const [bitForm, setBitForm] = useState(emptyBit);
  const [bundleForm, setBundleForm] = useState(emptyBundle);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data));
    api.get("/comedy").then((r) => setBits(r.data));
    api.get("/bundles").then((r) => setBundles(r.data));
    api.get("/admin/submissions").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading) {
      if (!user || !user.is_admin) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        load();
      }
    }
  }, [user, loading, navigate, load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setItemForm((p) => ({ ...p, preview_image: fullUrl }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const createItem = async (e) => {
    e.preventDefault();
    if (!itemForm.title || !itemForm.preview_image) {
      toast.error("Title and preview image required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/items", itemForm);
      toast.success("Item created");
      setItemForm(emptyItem);
      load();
    } catch {
      toast.error("Failed to create item");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const createBit = async (e) => {
    e.preventDefault();
    if (!bitForm.title || !bitForm.content) {
      toast.error("Title and content required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/comedy", bitForm);
      toast.success("Bit added");
      setBitForm(emptyBit);
      load();
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteBit = async (id) => {
    try {
      await api.delete(`/comedy/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleBundleItem = (id) => {
    setBundleForm((p) => ({
      ...p,
      item_ids: p.item_ids.includes(id) ? p.item_ids.filter((x) => x !== id) : [...p.item_ids, id],
    }));
  };

  const createBundle = async (e) => {
    e.preventDefault();
    if (!bundleForm.title || !bundleForm.cover_image || bundleForm.item_ids.length < 2) {
      toast.error("Title, cover image, and at least 2 items required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/bundles", bundleForm);
      toast.success("Bundle created");
      setBundleForm(emptyBundle);
      load();
    } catch {
      toast.error("Failed to create bundle");
    } finally {
      setSaving(false);
    }
  };

  const deleteBundle = async (id) => {
    try {
      await api.delete(`/bundles/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const reviewSubmission = async (kind, id, action) => {
    try {
      await api.post(`/admin/${kind}/${id}/review`, null, { params: { action } });
      toast.success(action === "approve" ? "Approved & live" : "Rejected");
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  if (loading || !user?.is_admin) {
    return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  }

  const statCards = stats
    ? [
        { label: "Items", value: stats.items, icon: Package, color: "#00E5FF" },
        { label: "Bundles", value: stats.bundles, icon: Stack, color: "#FFEB3B" },
        { label: "Comedy Bits", value: stats.comedy, icon: MicrophoneStage, color: "#FF00AA" },
        { label: "Premium Subs", value: stats.premium_subscriptions, icon: Crown, color: "#FFEB3B" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#05050A]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <GearSix weight="fill" size={24} className="text-[#FF00AA]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF00AA]">Content Management</span>
        </div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter uppercase">Admin Console</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-[#0A0A14] border border-white/10 rounded-sm p-5">
                <Icon size={22} style={{ color: s.color }} />
                <div className="font-display font-black text-3xl mt-3">{s.value}</div>
                <div className="text-white/40 text-xs uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-10 border-b border-white/10">
          {[
            { key: "items", label: "Items" },
            { key: "bundles", label: "Bundles" },
            { key: "comedy", label: "Comedy" },
            { key: "review", label: "Review", badge: (subs.items.length + subs.bundles.length) },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`relative px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                tab === t.key ? "border-[#00E5FF] text-[#00E5FF]" : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-black rounded-full bg-[#FF00AA] text-white" data-testid="review-badge">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "items" && (
          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            {/* Create form */}
            <form onSubmit={createItem} className="bg-[#0A0A14] border border-white/10 rounded-sm p-6 space-y-4" data-testid="item-create-form">
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2">
                <Plus size={18} /> New Item
              </h3>
              <Field label="Type">
                <select value={itemForm.type} onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })} className={inputCls} data-testid="item-type-select">
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Title">
                <input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className={inputCls} data-testid="item-title-input" />
              </Field>
              <Field label="Category">
                <input value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} className={inputCls} placeholder="e.g. Icon Pack" />
              </Field>
              <Field label="Description">
                <textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} className={inputCls} rows={3} />
              </Field>
              <Field label="Preview Image">
                <div className="flex gap-2">
                  <input value={itemForm.preview_image} onChange={(e) => setItemForm({ ...itemForm, preview_image: e.target.value })} className={inputCls} placeholder="Paste URL or upload" data-testid="item-image-input" />
                  <label className="shrink-0 flex items-center justify-center w-11 bg-[#05050A] border border-white/10 rounded-sm cursor-pointer hover:border-[#00E5FF] transition-colors">
                    <UploadSimple size={18} className={uploading ? "animate-pulse text-[#00E5FF]" : "text-white/60"} />
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="item-upload-input" />
                  </label>
                </div>
                {itemForm.preview_image && <img src={itemForm.preview_image} alt="" className="mt-3 w-full aspect-video object-cover rounded-sm border border-white/10" />}
              </Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={itemForm.featured} onChange={(e) => setItemForm({ ...itemForm, featured: e.target.checked })} className="accent-[#FFEB3B]" data-testid="item-featured-checkbox" />
                <span className="text-sm text-white/70">Feature on homepage</span>
              </label>
              <button type="submit" disabled={saving} className="w-full bg-[#00E5FF] text-[#05050A] font-bold py-3 rounded-sm hover:bg-[#5cf0ff] transition-colors disabled:opacity-60" data-testid="item-submit-btn">
                {saving ? "Saving…" : "Create Item"}
              </button>
            </form>

            {/* Item list */}
            <div className="lg:col-span-2 space-y-3" data-testid="admin-item-list">
              {items.map((it) => (
                <div key={it.id} className="bg-[#0A0A14] border border-white/10 rounded-sm p-3 flex items-center gap-4">
                  <img src={it.preview_image} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold truncate">{it.title}</p>
                      {it.featured && <Star weight="fill" size={14} className="text-[#FFEB3B] shrink-0" />}
                    </div>
                    <p className="text-white/40 text-xs">{TYPES.find((t) => t.value === it.type)?.label} · {it.download_count} downloads</p>
                  </div>
                  <button onClick={() => deleteItem(it.id)} data-testid={`delete-item-${it.id}`} className="text-white/40 hover:text-[#FF00AA] transition-colors p-2">
                    <Trash size={18} />
                  </button>
                </div>
              ))}
              {items.length === 0 && <p className="text-white/40 text-sm">No items.</p>}
            </div>
          </div>
        )}

        {tab === "bundles" && (
          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            <form onSubmit={createBundle} className="bg-[#0A0A14] border border-white/10 rounded-sm p-6 space-y-4" data-testid="bundle-create-form">
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><Plus size={18} /> New Bundle</h3>
              <Field label="Title">
                <input value={bundleForm.title} onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })} className={inputCls} data-testid="bundle-title-input" />
              </Field>
              <Field label="Description">
                <textarea value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} className={inputCls} rows={3} />
              </Field>
              <Field label="Cover Image URL">
                <input value={bundleForm.cover_image} onChange={(e) => setBundleForm({ ...bundleForm, cover_image: e.target.value })} className={inputCls} placeholder="Paste image URL" data-testid="bundle-cover-input" />
              </Field>
              <Field label={`Included Items (${bundleForm.item_ids.length} selected)`}>
                <div className="max-h-56 overflow-y-auto border border-white/10 rounded-sm divide-y divide-white/5" data-testid="bundle-item-picker">
                  {items.map((it) => (
                    <label key={it.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5">
                      <input type="checkbox" checked={bundleForm.item_ids.includes(it.id)} onChange={() => toggleBundleItem(it.id)} className="accent-[#FFEB3B]" data-testid={`bundle-pick-${it.id}`} />
                      <img src={it.preview_image} alt="" className="w-8 h-8 object-cover rounded-sm" />
                      <span className="text-sm truncate flex-1">{it.title}</span>
                      <span className="text-[10px] uppercase text-white/40">{TYPES.find((t) => t.value === it.type)?.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bundleForm.featured} onChange={(e) => setBundleForm({ ...bundleForm, featured: e.target.checked })} className="accent-[#FFEB3B]" data-testid="bundle-featured-checkbox" />
                <span className="text-sm text-white/70">Feature this bundle</span>
              </label>
              <button type="submit" disabled={saving} className="w-full bg-[#FFEB3B] text-[#05050A] font-bold py-3 rounded-sm hover:bg-[#fff35c] transition-colors disabled:opacity-60" data-testid="bundle-submit-btn">
                {saving ? "Saving…" : "Create Bundle"}
              </button>
            </form>

            <div className="lg:col-span-2 space-y-3" data-testid="admin-bundle-list">
              {bundles.map((b) => (
                <div key={b.id} className="bg-[#0A0A14] border border-white/10 rounded-sm p-3 flex items-center gap-4">
                  <img src={b.cover_image} alt="" className="w-16 h-12 object-cover rounded-sm shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold truncate">{b.title}</p>
                      {b.featured && <Star weight="fill" size={14} className="text-[#FFEB3B] shrink-0" />}
                    </div>
                    <p className="text-white/40 text-xs">{b.item_ids.length} items · {b.download_count} downloads</p>
                  </div>
                  <button onClick={() => deleteBundle(b.id)} data-testid={`delete-bundle-${b.id}`} className="text-white/40 hover:text-[#FF00AA] transition-colors p-2">
                    <Trash size={18} />
                  </button>
                </div>
              ))}
              {bundles.length === 0 && <p className="text-white/40 text-sm">No bundles.</p>}
            </div>
          </div>
        )}

        {tab === "comedy" && (
          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            <form onSubmit={createBit} className="bg-[#0A0A14] border border-white/10 rounded-sm p-6 space-y-4" data-testid="comedy-create-form">
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><Plus size={18} /> New Bit</h3>
              <Field label="Title">
                <input value={bitForm.title} onChange={(e) => setBitForm({ ...bitForm, title: e.target.value })} className={inputCls} data-testid="bit-title-input" />
              </Field>
              <Field label="Category">
                <input value={bitForm.category} onChange={(e) => setBitForm({ ...bitForm, category: e.target.value })} className={inputCls} placeholder="Bit / One-liner" />
              </Field>
              <Field label="Author">
                <input value={bitForm.author} onChange={(e) => setBitForm({ ...bitForm, author: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Content">
                <textarea value={bitForm.content} onChange={(e) => setBitForm({ ...bitForm, content: e.target.value })} className={inputCls} rows={5} data-testid="bit-content-input" />
              </Field>
              <button type="submit" disabled={saving} className="w-full bg-[#FF00AA] text-white font-bold py-3 rounded-sm hover:bg-[#ff3cc0] transition-colors disabled:opacity-60" data-testid="bit-submit-btn">
                {saving ? "Saving…" : "Add Bit"}
              </button>
            </form>

            <div className="lg:col-span-2 space-y-3" data-testid="admin-comedy-list">
              {bits.map((b) => (
                <div key={b.id} className="bg-[#0A0A14] border border-white/10 rounded-sm p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF00AA]">{b.category}</span>
                      <p className="font-bold">{b.title}</p>
                    </div>
                    <p className="text-white/50 text-sm">"{b.content}"</p>
                    <p className="text-white/30 text-xs mt-2">— {b.author}</p>
                  </div>
                  <button onClick={() => deleteBit(b.id)} data-testid={`delete-bit-${b.id}`} className="text-white/40 hover:text-[#FF00AA] transition-colors p-2 shrink-0">
                    <Trash size={18} />
                  </button>
                </div>
              ))}
              {bits.length === 0 && <p className="text-white/40 text-sm">No bits.</p>}
            </div>
          </div>
        )}

        {tab === "review" && (
          <div className="mt-8 space-y-10" data-testid="admin-review">
            {/* Pending submissions */}
            <div>
              <h3 className="font-display font-bold uppercase tracking-tight flex items-center gap-2 mb-4">
                <Package size={18} className="text-[#FFEB3B]" /> Pending Submissions
              </h3>
              <div className="space-y-3" data-testid="pending-submissions">
                {subs.items.length + subs.bundles.length === 0 && (
                  <p className="text-white/40 text-sm">No submissions awaiting review.</p>
                )}
                {[...subs.items.map((i) => ({ ...i, kind: "items", label: "Pack" })),
                  ...subs.bundles.map((b) => ({ ...b, kind: "bundles", label: "Bundle" }))].map((s) => (
                  <div key={s.id} className="bg-[#0A0A14] border border-white/10 rounded-sm p-3 flex items-center gap-4">
                    <img src={s.preview_image || s.cover_image} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#FFEB3B] bg-[#FFEB3B]/10 px-2 py-0.5 rounded-sm">{s.label}</span>
                        <p className="font-bold truncate">{s.title}</p>
                      </div>
                      <p className="text-white/40 text-xs mt-1">by {s.submitter_name || "creator"}</p>
                    </div>
                    <button onClick={() => reviewSubmission(s.kind, s.id, "approve")} data-testid={`approve-sub-${s.id}`}
                      className="flex items-center gap-1.5 bg-[#00E5FF] text-[#05050A] font-bold text-sm px-3.5 py-2 rounded-sm hover:bg-[#5cf0ff] transition-colors">
                      <CheckCircle weight="fill" size={16} /> Approve
                    </button>
                    <button onClick={() => reviewSubmission(s.kind, s.id, "reject")} data-testid={`reject-sub-${s.id}`}
                      className="p-2 text-white/40 hover:text-[#FF00AA] transition-colors"><XCircle size={20} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
